import { useScales } from '@Contexts/scales';
import { EnumWeighing, useWeighing } from '@Hooks/Orders';
import { type AsyncStateUpdate, isNullOrEmpty, useToasts, useStateFromProps } from '@synqo/frontend-component-library';
import { ApiWeighingSlip } from '@Model';
import { useCallback, useEffect } from 'react';

interface IUseWeighinSlipEdit {
    apiWeighingSlip: ApiWeighingSlip;
    requestUpdateState: AsyncStateUpdate<ApiWeighingSlip>;
}

export function useWeighingSlipEdit({ apiWeighingSlip, requestUpdateState }: IUseWeighinSlipEdit) {
    const { pushToast } = useToasts();
    const { selectedScale, canUseAutomaticWeighing } = useScales();
    const { setWeight, getWeight } = useWeighing();

    const isExternal = !isNullOrEmpty(apiWeighingSlip.external_id);

    const [deductionShown, setDeductionShown] = useStateFromProps(apiWeighingSlip.deduction !== 0);
    const [externalNumberShown, setExternalNumberShown] = useStateFromProps(isExternal);

    const hasWeights =
        apiWeighingSlip.gross_weight !== 0 || apiWeighingSlip.tare_weight !== 0 || apiWeighingSlip.deduction !== 0;

    useEffect(() => {
        if (selectedScale === undefined || hasWeights) {
            return;
        }
        if (apiWeighingSlip.unit === selectedScale.unit) {
            return;
        }

        apiWeighingSlip.unit = selectedScale.unit;
        requestUpdateState((latest, saveData) => {
            return saveData({
                ...latest,
                unit: selectedScale.unit,
            });
        });
    }, [apiWeighingSlip, selectedScale, hasWeights, requestUpdateState]);

    const doWeighing = useCallback(
        async (weighingMode: EnumWeighing): Promise<boolean> => {
            if (!canUseAutomaticWeighing || selectedScale === undefined) {
                pushToast({
                    title: 'Keine Waage ausgewählt',
                    message: 'Wählen sie zuerst eine Waage aus!',
                    variant: 'error',
                });
                return false;
            }

            return requestUpdateState(async (latest, saveData) => {
                const response = await getWeight(selectedScale);

                if (response === undefined) {
                    pushToast({
                        title: 'Verwiegung',
                        message: 'Verwiegung war nicht erfolgreich.',
                        variant: 'error',
                    });
                    return { data: undefined, messages: [] };
                }

                setWeight(latest, weighingMode, response);
                return saveData(latest);
            });
        },
        [canUseAutomaticWeighing, selectedScale, pushToast, requestUpdateState, getWeight, setWeight],
    );

    const doWeighIn = useCallback(() => doWeighing(EnumWeighing.Gross), [doWeighing]);
    const doWeighOut = useCallback(() => doWeighing(EnumWeighing.Tare), [doWeighing]);

    const showDeduction = useCallback(() => setDeductionShown(true), [setDeductionShown]);
    const removeDeduction = useCallback(() => {
        requestUpdateState((latest, saveData) => {
            setDeductionShown(false);
            return saveData({
                ...latest,
                deduction: 0,
                deduction_reason: '',
            });
        });
    }, [requestUpdateState, setDeductionShown]);

    const showExternalNumber = useCallback(() => setExternalNumberShown(true), [setExternalNumberShown]);

    return {
        hasWeights,
        doWeighIn,
        doWeighOut,

        deductionShown,
        showDeduction,
        removeDeduction,

        externalNumberShown,
        showExternalNumber,
    };
}
